/**
 * Eine Kartenbuchung anlegen, aendern oder loeschen. Eigene Formulare neben
 * AuszugFormulare.tsx, weil eine Kartenbuchung anders aussieht: Belastung
 * oder Gutschrift aus Sicht des Karteninhabers, Kaufdatum, Art der Zeile und
 * Fremdwaehrung. Der Betrag wird ohne Vorzeichen eingegeben, die Richtung
 * kommt nur aus dem Schalter.
 *
 * Jede Aenderung gilt danach als von Hand und nimmt eine Freigabe zurueck.
 */
import { useState, type FormEvent } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { api, ApiFehler, type Buchung, type Kartenklasse, type KartenZusatz } from '../lib/api'
import { datum } from '../lib/format'
import { betragOhneZeichen, KLASSE_TEXT, KLASSEN } from '../lib/karte'
import { Fenster } from './Fenster'

export function KartenBuchungBearbeiten({ auszugId, buchung, zusatz, offen, aufSchliessen }: {
  auszugId: string
  buchung: Buchung | null
  zusatz: KartenZusatz | null
  offen: boolean
  aufSchliessen: () => void
}) {
  const abfragen = useQueryClient()
  const neu = buchung === null
  const [werte, setzeWerte] = useState(() => ({
    buchungsdatum: buchung?.buchungsdatum ? datum(buchung.buchungsdatum) : '',
    belegdatum: zusatz?.belegdatum ? datum(zusatz.belegdatum) : '',
    betrag: buchung ? betragOhneZeichen(buchung.betrag_cent) : '',
    buchungstext: buchung?.buchungstext ?? '',
    originalbetrag: zusatz?.originalbetrag_cent ? betragOhneZeichen(zusatz.originalbetrag_cent) : '',
    originalwaehrung: zusatz?.originalwaehrung ?? '',
    kurs: zusatz?.kurs ?? '',
  }))
  const [richtung, setzeRichtung] = useState<'CRDT' | 'DBIT'>(buchung?.richtung ?? 'DBIT')
  const [klasse, setzeKlasse] = useState<Kartenklasse | null>(zusatz?.klasse ?? null)
  const [fehler, setzeFehler] = useState<{ text: string; feld?: string } | null>(null)
  const [laeuft, setzeLaeuft] = useState(false)
  const [loeschenFragen, setzeLoeschenFragen] = useState(false)

  const feld = (name: keyof typeof werte) => ({
    value: werte[name],
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => setzeWerte({ ...werte, [name]: name === 'originalwaehrung' ? e.target.value.toUpperCase().slice(0, 3) : e.target.value }),
    'aria-invalid': fehler?.feld === name,
    autoComplete: 'off',
    'data-1p-ignore': true,
  })

  function problemText(problem: unknown, rueckfall: string) {
    return {
      text: problem instanceof ApiFehler ? problem.message : rueckfall,
      ...(problem instanceof ApiFehler && problem.feld ? { feld: problem.feld } : {}),
    }
  }

  async function absenden(e: FormEvent) {
    e.preventDefault()
    setzeFehler(null)
    setzeLaeuft(true)
    const koerper = {
      buchungsdatum: werte.buchungsdatum.trim(),
      belegdatum: werte.belegdatum.trim() || null,
      betrag: werte.betrag.trim(),
      richtung,
      buchungstext: werte.buchungstext.trim() || null,
      klasse,
      originalbetrag: werte.originalbetrag.trim() || null,
      originalwaehrung: werte.originalwaehrung.trim() || null,
      kurs: werte.kurs.trim() || null,
    }
    try {
      if (neu) await api.senden(`/api/karten/abrechnung/${auszugId}/buchungen`, koerper)
      else await api.aendern(`/api/karten/abrechnung/${auszugId}/buchungen/${buchung.id}`, koerper)
      await abfragen.invalidateQueries()
      aufSchliessen()
    } catch (problem) {
      setzeFehler(problemText(problem, 'Die Buchung konnte nicht gespeichert werden.'))
    } finally {
      setzeLaeuft(false)
    }
  }

  async function loeschen() {
    if (!buchung) return
    setzeLaeuft(true)
    try {
      await api.loeschen(`/api/karten/abrechnung/${auszugId}/buchungen/${buchung.id}`)
      await abfragen.invalidateQueries()
      aufSchliessen()
    } catch (problem) {
      setzeFehler(problemText(problem, 'Die Buchung konnte nicht gelöscht werden.'))
    } finally {
      setzeLaeuft(false)
    }
  }

  return (
    <Fenster titel={neu ? 'Buchung hinzufügen' : `Buchung ${buchung.position}`} offen={offen} aufSchliessen={aufSchliessen} breit>
      <form className="formular" onSubmit={(e) => void absenden(e)}>
        {fehler && <div className="fehler">{fehler.text}</div>}
        {buchung?.betrag_gedruckt && (
          <div className="leiser">So gelesen: <span className="zahl">{buchung.betrag_gedruckt}</span>{buchung.seite ? `, Seite ${buchung.seite}` : ''}</div>
        )}
        <div className="feldreihe">
          <div><label htmlFor="kb-dat">Buchungsdatum</label><input id="kb-dat" type="text" className="zahl" placeholder="TT.MM.JJJJ" required {...feld('buchungsdatum')} /></div>
          <div><label htmlFor="kb-kauf">Kaufdatum</label><input id="kb-kauf" type="text" className="zahl" placeholder="TT.MM.JJJJ" {...feld('belegdatum')} /></div>
          <div><label htmlFor="kb-betrag">Betrag in Euro</label><input id="kb-betrag" type="text" className="zahl" placeholder="1.234,56" required {...feld('betrag')} /></div>
        </div>
        <div>
          <label>Richtung aus Sicht des Karteninhabers</label>
          <div className="wahl" role="group" aria-label="Richtung">
            <button type="button" aria-pressed={richtung === 'DBIT'} onClick={() => setzeRichtung('DBIT')}>Belastung</button>
            <button type="button" aria-pressed={richtung === 'CRDT'} onClick={() => setzeRichtung('CRDT')}>Gutschrift</button>
          </div>
          <div className="hinweis">Belastung erhöht die Schuld (Kauf, Entgelt, Zinsen). Gutschrift senkt sie (Zahlung vom Girokonto, Erstattung).</div>
        </div>
        <div><label htmlFor="kb-text">Buchungstext oder Händler</label><input id="kb-text" type="text" {...feld('buchungstext')} /></div>
        <div>
          <label>Art der Zeile</label>
          <div className="wahl" role="group" aria-label="Art der Zeile" style={{ flexWrap: 'wrap' }}>
            {KLASSEN.map((k) => (
              <button key={k} type="button" aria-pressed={klasse === k} onClick={() => setzeKlasse(klasse === k ? null : k)}>{KLASSE_TEXT[k]}</button>
            ))}
          </div>
        </div>
        <div className="feldreihe">
          <div><label htmlFor="kb-ob">Originalbetrag</label><input id="kb-ob" type="text" className="zahl" placeholder="nur bei Fremdwährung" {...feld('originalbetrag')} /></div>
          <div className="schmal"><label htmlFor="kb-ow">Währung</label><input id="kb-ow" type="text" className="zahl" placeholder="USD" {...feld('originalwaehrung')} /></div>
          <div className="schmal"><label htmlFor="kb-kurs">Kurs</label><input id="kb-kurs" type="text" className="zahl" {...feld('kurs')} /></div>
        </div>
        <div className="formular-fuss">
          {!neu && !loeschenFragen && <button type="button" className="knopf still links" onClick={() => setzeLoeschenFragen(true)}>Buchung löschen</button>}
          {!neu && loeschenFragen && (
            <span className="links rueckfrage">
              Wirklich löschen?
              <button type="button" className="knopf klein" onClick={() => void loeschen()} disabled={laeuft}>Löschen</button>
              <button type="button" className="knopf still klein" onClick={() => setzeLoeschenFragen(false)}>Nein</button>
            </span>
          )}
          <button type="button" className="knopf still" onClick={aufSchliessen}>Abbrechen</button>
          <button type="submit" className="knopf gefuellt" disabled={laeuft}>{laeuft ? 'Wird gespeichert …' : neu ? 'Hinzufügen' : 'Speichern'}</button>
        </div>
      </form>
    </Fenster>
  )
}
